// Copyright (c) 2020 WiseTime. All rights reserved.

import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import type { RootState } from "app/store"
import { DialogStatus } from "features/shared/Dialog"
import * as Actions from "./actions"
import * as UserContextSlice from "../user-context/slice"
import { naturalOrderCollator } from "../../lib/utils"
import { Tag, Tag_SuggestionConfig, TagKeyword, TagShallow } from "generated/legebill/type/tag_pb"
import {
  FavouriteTag,
  ListFavouriteTagsResponse,
  ListRecentTagsResponse,
  SearchTagsRequest_OrderBy,
  SearchTagsResponse,
  SearchTagsResponseShallow,
} from "generated/legebill/site/tag/v4/tag_pb"
import { TagCategoriesCache, TagsCache } from "./types"
import { TagCategory } from "../../generated/legebill/type/tag_category_pb"

export type SearchTagsResult = {
  searchString: string,
  response: SearchTagsResponse,
}

export type SearchTagsResultShallow = {
  searchString: string,
  response: SearchTagsResponseShallow,
}

export enum RecentTagsOrderBy {
  RELEVANCE = 1,
  NAME = 2,
}

export enum ViewMode {
  SEARCH = 1,
  BROWSE = 2,
}

export type TagsState = {
  recentTags: Tag[],
  favouriteTags: FavouriteTag[],
  favouriteTagsInitiallyLoaded: boolean,
  // Searched tags result
  resultTags: Tag[],
  resultTagsShallow: TagShallow[],
  isSearching: boolean,
  searchString: string,
  searchOrderBy: SearchTagsRequest_OrderBy,
  recentOrderBy: RecentTagsOrderBy,
  viewMode: ViewMode,
  tagsCache: TagsCache,
  tagCategoriesCache: TagCategoriesCache,
  dialogStatus: DialogStatus,
  isInitialBatchLoaded: boolean,
}

export const initialState: TagsState = {
  recentTags: [],
  favouriteTags: [],
  favouriteTagsInitiallyLoaded: false,
  resultTags: [],
  resultTagsShallow: [],
  isSearching: false,
  searchString: "",
  searchOrderBy: SearchTagsRequest_OrderBy.RELEVANCE,
  recentOrderBy: RecentTagsOrderBy.RELEVANCE,
  viewMode: ViewMode.SEARCH,
  tagsCache: {},
  tagCategoriesCache: {},
  dialogStatus: "CLOSED",
  isInitialBatchLoaded: false,
}

export type UpdateTagResult = {
  tagId: string,
  name: string,
  description: string,
  suggestionConfig: Tag_SuggestionConfig,
}

export const slice = createSlice({
  name: "tags",
  initialState,
  reducers: {
    setRecentTags: (state, action: PayloadAction<ListRecentTagsResponse>): void => {
      state.recentTags = action.payload.recentTags
      action.payload.recentTags.forEach((recentTag: Tag) => {
        state.tagsCache[recentTag.id] = recentTag
      })
    },
    setResultTags: (state, action: PayloadAction<SearchTagsResult>): void => {
      if (action.payload.searchString === state.searchString) {
        state.resultTags = action.payload.response.tags
        state.isSearching = initialState.isSearching
        state.searchString = initialState.searchString
        action.payload.response.tags.forEach((resultTag: Tag) => {
          state.tagsCache[resultTag.id] = resultTag
        })
      }
    },
    setResultTagsShallow: (state, action: PayloadAction<SearchTagsResultShallow>): void => {
      if (action.payload.searchString === state.searchString) {
        state.resultTagsShallow = action.payload.response.tagsShallow
        state.isSearching = initialState.isSearching
        state.searchString = initialState.searchString
      }
    },
    resetResultTagsShallow: (state): void => {
      state.resultTagsShallow = initialState.resultTagsShallow
      state.isSearching = initialState.isSearching
    },
    resetResultTags: (state): void => {
      state.resultTags = initialState.resultTags
      state.isSearching = initialState.isSearching
    },
    setFavouriteTags: (state, action: PayloadAction<ListFavouriteTagsResponse>): void => {
      state.favouriteTags = action.payload.favouriteTags
      action.payload.favouriteTags.forEach((favouriteTag: FavouriteTag) => {
        const { tag } = favouriteTag
        if (tag) {
          state.tagsCache[tag.id] = tag
        }
      })
      state.favouriteTagsInitiallyLoaded = true
    },
    createFavouriteTagFulfilled: (state, action: PayloadAction<FavouriteTag>): void => {
      state.favouriteTags.unshift(action.payload)
    },
    setTags: (state, action: PayloadAction<Tag[]>): void => {
      action.payload.forEach((tag: Tag) => {
        state.tagsCache[tag.id] = tag
      })
      state.isInitialBatchLoaded = true
    },
    setTagCategories: (state, action: PayloadAction<TagCategory[]>): void => {
      action.payload.forEach(tagCategory => {
        state.tagCategoriesCache[tagCategory.id] = tagCategory
      })
    },
    updateTag: (state, action: PayloadAction<UpdateTagResult>): void => {
      const { tagId, name, description, suggestionConfig } = action.payload
      const tag = state.tagsCache[tagId]
      if (tag) {
        tag.name = name
        tag.description = description
        tag.suggestionConfig = suggestionConfig
      }
    },
    setTag: (state, action: PayloadAction<Tag>): void => {
      state.tagsCache[action.payload.id] = action.payload
    },
    createTagKeywordFulfilled: (state, action: PayloadAction<TagKeyword>): void => {
      state.tagsCache[action.payload.tagId]?.keywords.push(action.payload)
    },
    deleteTagKeywordFulfilled: (state, action: PayloadAction<TagKeyword>): void => {
      const cachedTag = state.tagsCache[action.payload.tagId]
      if (cachedTag !== undefined) {
        cachedTag.keywords = cachedTag.keywords.filter(keyword => action.payload.text !== keyword.text)
      }
    },
    setSearchOrderBy: (state, action: PayloadAction<SearchTagsRequest_OrderBy>): void => {
      state.searchOrderBy = action.payload
    },
    setRecentOrderBy: (state, action: PayloadAction<RecentTagsOrderBy>): void => {
      state.recentOrderBy = action.payload
    },
    setViewMode: (state, action: PayloadAction<ViewMode>): void => {
      state.viewMode = action.payload
    },
    setDialogStatus: (state, action: PayloadAction<DialogStatus>): void => {
      state.dialogStatus = action.payload
    },
    clearRecentAndFavouriteTags: (state): void => {
      state.recentTags = []
      state.favouriteTags = []
    },
    removeFromCache: (state, action: PayloadAction<string>): void => {
      delete state.tagsCache[action.payload]
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(Actions.searchTags, (state, action) => {
        state.isSearching = true
        state.searchString = action.payload.searchText ?? ""
      })
      .addCase(Actions.searchTagsShallow, (state, action) => {
        state.isSearching = true
        state.searchString = action.payload.searchText ?? ""
      })
      .addCase(UserContextSlice.setCurrentSiteId, (state) => {
        state.resultTagsShallow = initialState.resultTagsShallow
        state.favouriteTagsInitiallyLoaded = initialState.favouriteTagsInitiallyLoaded
      })
  },
})

export const {
  clearRecentAndFavouriteTags,
  createFavouriteTagFulfilled,
  createTagKeywordFulfilled,
  deleteTagKeywordFulfilled,
  removeFromCache,
  resetResultTags,
  resetResultTagsShallow,
  setDialogStatus,
  setFavouriteTags,
  setRecentTags,
  setResultTags,
  setResultTagsShallow,
  setTags,
  setTagCategories,
  setTag,
  setSearchOrderBy,
  setRecentOrderBy,
  setViewMode,
  updateTag,
} = slice.actions

// Use createSelector to generate memoized selectors
// https://redux.js.org/usage/deriving-data-selectors#createselector-overview
export const selectRecentTags = createSelector(
  (state: RootState): Tag[] => state.tags.recentTags,
  (state: RootState): RecentTagsOrderBy => state.tags.recentOrderBy,
  (recentTags, recentOrderBy) => {
    if (recentOrderBy === RecentTagsOrderBy.NAME) {
      return recentTags.slice().sort((a, b) => a.name.localeCompare(b.name))
    }
    return recentTags
  }
)
export const selectResultTags = (state: RootState): Tag[] => state.tags.resultTags
export const selectResultTagsShallow = (state: RootState): TagShallow[] => state.tags.resultTagsShallow
export const selectIsSearching = (state: RootState): boolean => state.tags.isSearching
export const selectFavouriteTags = (state: RootState): FavouriteTag[] => state.tags.favouriteTags
export const selectFavouriteTagsInitiallyLoaded = (state: RootState): boolean =>
  state.tags.favouriteTagsInitiallyLoaded
export const selectTagsCache = (state: RootState): TagsCache => state.tags.tagsCache
export const selectTagCategoriesCache = (state: RootState): TagCategoriesCache => state.tags.tagCategoriesCache
export const selectTagCategoryById = () => createSelector(
  (_: RootState, tagCategoryId: string) => tagCategoryId,
  selectTagCategoriesCache,
  (tagCategoryId, tagCategoriesCache): TagCategory | undefined => tagCategoriesCache[tagCategoryId]
)
export const selectFavouriteTagId = () => createSelector(
  (_: RootState, tagId: string) => tagId,
  selectFavouriteTags,
  (tagId, favouriteTags): string | undefined =>
    favouriteTags.find(t => t.tag?.id === tagId)?.id,
)
export const selectTagsByCategoryId = () => createSelector(
  (_: RootState, tagCategoryId: string) => tagCategoryId,
  selectTagCategoriesCache,
  selectTagsCache,
  (tagCategoryId, tagCategoriesCache, tagsCache): Tag[] | undefined => {
    if (tagCategoriesCache[tagCategoryId] === undefined) {
      return undefined
    }
    return Object.values(tagsCache)
      .filter(t => t.tagCategoryId === tagCategoryId)
      .sort((t1, t2) => naturalOrderCollator.compare(t1.name, t2.name))
  }
)
export const selectSearchOrderBy = (state: RootState): SearchTagsRequest_OrderBy => state.tags.searchOrderBy
export const selectRecentOrderBy = (state: RootState): RecentTagsOrderBy => state.tags.recentOrderBy
export const selectViewMode = (state: RootState): ViewMode => state.tags.viewMode
export const selectDialogStatus = (state: RootState): DialogStatus => state.tags.dialogStatus
export const selectIsInitialBatchLoaded = (state: RootState): boolean => state.tags.isInitialBatchLoaded

export const selectTag = () => createSelector(
  (_: RootState, tagId: string) => tagId,
  (state: RootState): TagsCache => state.tags.tagsCache,
  (tagId: string, tagsCache: TagsCache): Tag | undefined => tagsCache[tagId],
)

export default slice.reducer
